import { Position } from "@/services/backend/vbs/treatments/types/position";
import { HTMLAttributes } from "react";
import { cn } from "@/shared/lib/utils";
import { useTreatmentComponentState } from "@/services/backend/vbs/treatments/components/treatment-component-state-context";
import { FinalPriceCell } from "@/services/backend/vbs/treatments/components/final-price-cell";
import {
  ALL_COLUMNS,
  Column,
} from "@/services/backend/vbs/treatments/lib/treatment-component-state";
import { BasePriceCell } from "@/services/backend/vbs/treatments/components/base-price-cell";
import { CycleCell } from "@/services/backend/vbs/treatments/components/cycle-cell";
import { CycleDiscountCell } from "@/services/backend/vbs/treatments/components/cycle-discount-cell";
import { SpecialDiscountCell } from "@/services/backend/vbs/treatments/components/special-discount-cell";
import { KindCell } from "./kind-cell";
import { ComponentCell } from "./component-cell";
import { FormulaCell } from "./formula-cell";
import { CleanTimeCell } from "./clean-time-cell";
import { CommentCell } from "./comment-cell";
import { CompletedCell } from "./completed-cell";
import { DeletePositionDialog } from "./delete-position-dialog";

export function PositionRow({
  treatmentId,
  position,
}: {
  treatmentId: string;
  position: Position;
}) {
  const { state, disabled } = useTreatmentComponentState();
  const { columns } = state;

  return (
    <tr className="border-t align-top">
      {ALL_COLUMNS.map((column) => {
        if (!columns.includes(column)) {
          return null;
        }
        return (
          <Cell
            key={column}
            column={column}
            treatmentId={treatmentId}
            position={position}
            disabled={disabled}
          />
        );
      })}
    </tr>
  );
}

function Cell({
  treatmentId,
  column,
  position,
  disabled = false,
}: {
  treatmentId: string;
  column: Column;
  position: Position;
  disabled?: boolean;
}) {
  switch (column) {
    case Column.Position:
      return (
        <TD className="font-bold">
          <span className="flex h-6 items-center">{position.number}</span>
        </TD>
      );
    case Column.Completed:
      return (
        <TD className="flex justify-start">
          <CompletedCell
            treatmentId={treatmentId}
            position={position}
            className="h-5 w-5"
          />
        </TD>
      );
    case Column.ComponentKind:
      return (
        <TD>
          <KindCell kind={position.component.kind} />
        </TD>
      );
    case Column.ComponentData:
      return (
        <TD>
          <ComponentCell
            treatmentId={treatmentId}
            positionId={position.id}
            component={position.component}
            disabled={disabled}
          />
        </TD>
      );
    case Column.Cycle:
      return (
        <TD className="text-right">
          <CycleCell
            treatmentId={treatmentId}
            position={position}
            disabled={disabled}
          />
        </TD>
      );
    case Column.Formula:
      return (
        <TD>
          <FormulaCell
            treatmentId={treatmentId}
            positionId={position.id}
            formula={position.formula}
            disabled={disabled}
          />
        </TD>
      );
    case Column.CleanTime:
      return (
        <TD className="text-right">
          <CleanTimeCell postion={position} />
        </TD>
      );
    case Column.BasePrice:
      return (
        <TD className="text-right">
          <BasePriceCell position={position} />
        </TD>
      );
    case Column.CycleDiscount:
      return (
        <TD className="text-right">
          <CycleDiscountCell
            treatmentId={treatmentId}
            position={position}
            disabled={disabled}
          />
        </TD>
      );
    case Column.SpecialDiscount:
      return (
        <TD className="text-right">
          <SpecialDiscountCell
            treatmentId={treatmentId}
            position={position}
            disabled={disabled}
          />
        </TD>
      );
    case Column.FinalPrice:
      return (
        <TD className="text-right">
          <FinalPriceCell position={position} />
        </TD>
      );
    case Column.ActuallyTreated:
      return (
        <TD>
          <ComponentCell
            treatmentId={treatmentId}
            positionId={position.id}
            component={position.actuallyTreated}
            actuallyTreated
            disabled={disabled}
          />
        </TD>
      );
    case Column.Comment:
      return (
        <TD>
          <CommentCell
            treatmentId={treatmentId}
            positionId={position.id}
            comment={position.comment}
            disabled={disabled}
          />
        </TD>
      );
    case Column.Actions:
      return (
        <TD className="flex justify-end space-x-2 px-3 py-2">
          {!disabled && (
            <DeletePositionDialog
              treatmentId={treatmentId}
              position={position}
            />
          )}
        </TD>
      );
    default:
      return null;
  }
}

type TDProps = HTMLAttributes<HTMLTableCellElement>;

function TD({ children, className }: TDProps) {
  return <th className={cn("px-6 py-3 font-normal", className)}>{children}</th>;
}
